import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectComponentsItems } from '../../store/control-service.selectors';
import { Component as ComponentItem } from '@shared/data-provider/app-supporter';
import { ControlServiceComponentActions } from '../../store/control-service.actions';
import { GlobalI18n } from '@settings/global-i18n';
import { Module } from '@shared/models/module-name';

export interface ComponentGroup {
  org: string;
  children: ComponentItem[];
}

@Component({
  selector: 'app-control',
  standalone: false,
  templateUrl: './control.component.html',
})
export class ControlComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly globalI18n = inject(GlobalI18n);

  readonly components = this.store.selectSignal(selectComponentsItems);
  readonly expandedOrgs = signal(new Set<string>());
  showComponentModal = false;

  readonly tableScroll = { y: 'calc(100vh - 220px)' };

  readonly groups = computed<ComponentGroup[]>(() => {
    const items = this.components() ?? [];
    const map = new Map<string, ComponentItem[]>();

    for (const item of items) {
      const org = item.org ?? '';
      const list = map.get(org);
      if (list) {
        list.push(item);
      } else {
        map.set(org, [item]);
      }
    }

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([org, children]) => ({ org, children }));
  });

  readonly listOfColumns = [
    { name: this.globalI18n.getMessage(Module.ControlService, 'columnProject') },
    { name: this.globalI18n.getMessage(Module.ControlService, 'columnComponent') },
  ];

  readonly orgColumnName = this.globalI18n.getMessage(Module.ControlService, 'columnOrganization');

  readonly orgFilters = computed(() =>
    this.groups().map(group => ({ text: group.org, value: group.org }))
  );

  readonly filterOrg = (selected: string[], group: ComponentGroup): boolean =>
    !selected?.length || selected.includes(group.org);

  readonly compareOrg = (a: ComponentGroup, b: ComponentGroup): number =>
    a.org.localeCompare(b.org, undefined, { sensitivity: 'base' });

  ngOnInit() {
    this.store.dispatch(ControlServiceComponentActions.readAll());
  }

  isExpanded(org: string): boolean {
    return this.expandedOrgs().has(org);
  }

  onExpandChange(org: string, expanded: boolean): void {
    this.expandedOrgs.update(current => {
      const next = new Set(current);
      if (expanded) {
        next.add(org);
      } else {
        next.delete(org);
      }
      return next;
    });
  }

  showNewComponentModal(): void {
    this.showComponentModal = true;
  }

  componentNewModalIsVisibleChange(visibility: boolean) {
    this.showComponentModal = visibility;
  }
}
